import { Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CardTitle from "@saleor/components/CardTitle";
import ExternalLink from "@saleor/components/ExternalLink";
import { Hr } from "@saleor/components/Hr";
import Skeleton from "@saleor/components/Skeleton";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  addressLineOne,
  addressLineThree,
  addressLineTwo
} from "@saleor/motherOrders/components/OrderCustomer/handlers";
import { StoreOrderDetails_storeOrder } from "@saleor/storeOrders/types/StoreOrderDetails";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage } from "react-intl";

import { maybe } from "../../../misc";

const useStyles = makeStyles(
  theme => ({
    copyBtn: {
      padding: "0px 0px 0px 5px"
    },
    root: {
      ...theme.typography.body1,
      lineHeight: 1.9,
      width: "100%"
    },
    subSection: {
      display: "flex",
      flexDirection: "column",
      marginTop: "9px"
    },
    subSectionTitle: {
      flex: 1,
      fontWeight: 500,
      lineHeight: 1,
      marginTop: "0px"
    },
    textBold: {
      fontSize: "14px",
      fontWeight: 600,
      whiteSpace: "nowrap"
    },
    textRight: {
      fontSize: "14px",
      textAlign: "right",
      whiteSpace: "nowrap"
    },
    title: {
      flex: 1,
      fontWeight: 500,
      lineHeight: 1
    }
  }),
  { name: "StoreOrderCustomerInfo" }
);

export interface StoreOrderCustomerInfo {
  storeOrder: StoreOrderDetails_storeOrder;
  userType: SmopUserTypeCode;
}

const StoreOrderCustomer: React.FC<StoreOrderCustomerInfo> = props => {
  const { storeOrder, userType } = props;
  const classes = useStyles(props);
  const notify = useNotifier();

  const copyToClipboard = text => {
    if (text && text.length > 0) {
      navigator.clipboard.writeText(text);
      notify({
        status: "success",
        text: "SMAC No. copied to clipboard"
      });
    }
  };

  return (
    <Card>
      <CardTitle title="Customer" />
      <CardContent>
        <table className={classes.root}>
          <tbody>
            <tr>
              <td className={classes.textBold}>
                <FormattedMessage
                  defaultMessage="Name"
                  description="order customer name"
                />
              </td>
              <td className={classes.textRight}>
                {storeOrder &&
                storeOrder.order &&
                storeOrder.order.user &&
                storeOrder.order.user.firstName
                  ? storeOrder.order.user.firstName + " "
                  : "-"}
                {storeOrder &&
                storeOrder.order &&
                storeOrder.order.user &&
                storeOrder.order.user.lastName
                  ? storeOrder.order.user.lastName
                  : "-"}
              </td>
            </tr>
            {(userType && userType !== "STORE_ADMIN") || !userType ? (
              <tr>
                <td className={classes.textBold}>
                  <FormattedMessage
                    defaultMessage="SMAC No."
                    description="SMAC Number"
                  />
                </td>
                <td className={classes.textRight}>
                  {maybe(() => storeOrder?.smac) === undefined ? (
                    <Skeleton />
                  ) : storeOrder?.smac === null ? (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Not set"
                        description="customer is not set in order"
                        id="orderCustomerCustomerNotSet"
                      />
                    </Typography>
                  ) : (
                    <div id="smacno">
                      <span id="smacnoTxt">{storeOrder?.smac}</span>
                      <IconButton
                        id="orderCustomerSMACNoCopyBtn"
                        aria-label="copy"
                        color="primary"
                        className={classes.copyBtn}
                        onClick={() => copyToClipboard(storeOrder?.smac)}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className={classes.textBold}>
                <FormattedMessage
                  defaultMessage="Email Address"
                  description="order customer email"
                />
              </td>
              <td className={classes.textRight}>
                {maybe(() => storeOrder.order.user.email) === undefined ? (
                  <Skeleton />
                ) : storeOrder.order.user.email === null ? (
                  <Typography>
                    <FormattedMessage
                      defaultMessage="Not set"
                      description="customer is not set in order"
                      id="orderCustomerCustomerNotSet"
                    />
                  </Typography>
                ) : (
                  <ExternalLink
                    href={`mailto:${maybe(() => storeOrder.order.user.email)}`}
                    typographyProps={{ color: "primary" }}
                  >
                    {maybe(() => storeOrder.order.user.email, "")}
                  </ExternalLink>
                )}
              </td>
            </tr>
            <tr>
              <td className={classes.textBold}>
                <FormattedMessage
                  defaultMessage="Contact Number"
                  description="order customer contact number"
                />
              </td>
              <td className={classes.textRight}>
                {maybe(() => storeOrder.order.user.contactNo) === undefined ? (
                  <Skeleton />
                ) : storeOrder.order.user.contactNo === null ? (
                  <Typography>
                    <FormattedMessage
                      defaultMessage="Not set"
                      description="customer is not set in order"
                      id="orderCustomerCustomerNotSet"
                    />
                  </Typography>
                ) : (
                  <ExternalLink
                    href={`tel:${maybe(() => storeOrder.order.user.contactNo)}`}
                    typographyProps={{ color: "primary" }}
                  >
                    {maybe(() => storeOrder.order.user.contactNo, "")}
                  </ExternalLink>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
      <Hr />
      <CardContent>
        <h2 className={classes.subSectionTitle}>Shipping Address</h2>
        <div className={classes.subSection}>
          <span>{addressLineOne(storeOrder?.order)}</span>
        </div>
        <div className={classes.subSection}>
          <span>{addressLineTwo(storeOrder?.order)}</span>
        </div>

        <div className={classes.subSection}>
          <span>{addressLineThree(storeOrder?.order)}</span>
        </div>
        <div className={classes.subSection}>
          <span className={classes.textBold}>Latitude and Longitude</span>
          <span>
            {(() => {
              const lat = maybe(
                () => storeOrder.order.shippingAddress.latitude
              );
              const lng = maybe(
                () => storeOrder.order.shippingAddress.longitude
              );
              return `${lat ?? "--"}, ${lng ?? "--"}`;
            })()}
          </span>
        </div>
        <div className={classes.subSection}>
          <span className={classes.textBold}>
            Distance from {maybe(() => storeOrder.order.mall.name)}
          </span>
          <span>{maybe(() => storeOrder.order.distanceFromMall)}KM</span>
        </div>
      </CardContent>
    </Card>
  );
};
StoreOrderCustomer.displayName = "Customer";
export default StoreOrderCustomer;
